/**
 * Persisted user settings. localStorage-backed; mirrors Android's
 * SharedPreferences-backed `Settings.kt`. Defaults match the Android app's
 * conservative-by-default choices.
 */

export type BgMode = "DETECT" | "BLACK" | "COLOR" | "SHUFFLE";

export type VariantId = "q4f16" | "q4" | "quantized" | "fp16";

export interface SettingsValues {
  bgMode: BgMode;
  /** ARGB-packed (0xAARRGGBB). The exported PNG is always opaque; alpha is
   *  ignored on export — preview alpha lives separately. */
  bgColor: number;
  /** 0–255. Alpha applied to the redaction overlay in the editor canvas. */
  previewAlpha: number;
  /** When true, the export shrinks the bitmap before encoding the PNG. */
  resizeBeforeShare: boolean;
  /** Snapped to a 10-step in [10, 90]. Ignored when resizeBeforeShare is off. */
  resizeSharePercent: number;
  /** Auto-redact a band at the top of the image equal to a typical mobile
   *  status-bar height. Off by default on the web — there's no platform
   *  status bar to predict. */
  redactStatusBar: boolean;
  variantId: VariantId;
  /** When true, the editor canvas overlays each OCR token's bounding box
   *  (and recognised text) on top of the image. Lets users see what
   *  Tesseract picked up before the privacy filter ran. */
  debugShowOcr: boolean;
}

const DEFAULTS: SettingsValues = {
  bgMode: "DETECT",
  bgColor: 0xff000000,
  previewAlpha: 153,
  resizeBeforeShare: false,
  resizeSharePercent: 50,
  redactStatusBar: false,
  variantId: "q4f16",
  debugShowOcr: false,
};

const KEY = "redact_settings_v1";

export function loadSettings(): SettingsValues {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return { ...DEFAULTS };
    const parsed = JSON.parse(raw) as Partial<SettingsValues>;
    return {
      ...DEFAULTS,
      ...parsed,
      previewAlpha: clamp(parsed.previewAlpha ?? DEFAULTS.previewAlpha, 0, 255),
      resizeSharePercent: snap10(clamp(parsed.resizeSharePercent ?? DEFAULTS.resizeSharePercent, 10, 90)),
    };
  } catch {
    return { ...DEFAULTS };
  }
}

export function saveSettings(s: SettingsValues): void {
  localStorage.setItem(KEY, JSON.stringify(s));
}

function clamp(n: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, n | 0));
}
function snap10(n: number): number {
  return Math.floor((n + 5) / 10) * 10;
}

/** Pack an `#rrggbb` hex string into our ARGB number. Alpha is forced to 255. */
export function hexToArgb(hex: string): number {
  const m = /^#?([0-9a-fA-F]{6})$/.exec(hex.trim());
  if (!m) return 0xff000000;
  const v = parseInt(m[1], 16);
  return 0xff000000 | v;
}

/** ARGB number → `#rrggbb` string (alpha discarded). */
export function argbToHex(argb: number): string {
  const v = argb & 0xffffff;
  return "#" + v.toString(16).padStart(6, "0");
}
